import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import * as DateHelpers from './DateHelpers';
import axios from 'axios';
import authService from './api-authorization/AuthorizeService';
import { toast } from 'react-toastify';

interface AllocateProps {
    scheduleId: number;
    jobQuoteItemId: number;
    showExtendModal: boolean;
    hideModal(): void;
    save(): void;
}

interface ExtendDetails {
    scheduleId: number;
    typeId: number; //schedule type => 1: Install, 2: Pick up
    itemId: number;
    item: string;
    lastHireDate: string;
    existingLastHireDate: string;
    offhireDate: string;
    existingOffhireDate: string;
    existingPickupDate: string;
    existingOverhireRate: number;
    lastInvoiceDate: string;
    lastInvoiceDateFormatted: string;
    accountTypeId: number;
    purchaseOrderNumber: string;
    extendNoDays: number;
    newLastHireDate: string;
    newOffhireDate: string;
    newPickupDate: string;
    invoiceAmount: number;
    discountPercent: number;
    publicHolidays: number;
    finalInvoiceAmount: number;
}

const AllocateExtendModal = (props: AllocateProps) => {
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [extendDetails, setExtendDetails] = React.useState<ExtendDetails>({
        scheduleId: 0,
        typeId: 0,
        itemId: 0,
        item: "",
        lastHireDate: "",
        existingLastHireDate: "",
        offhireDate: "",
        existingOffhireDate: "",
        existingPickupDate: "",
        existingOverhireRate: 0,
        lastInvoiceDate: "",
        lastInvoiceDateFormatted: "",
        accountTypeId: 0,
        purchaseOrderNumber: "",
        extendNoDays: 0,
        newLastHireDate: "",
        newOffhireDate: "",
        newPickupDate: "",
        invoiceAmount: 0,
        discountPercent: 0,
        publicHolidays: 0,
        finalInvoiceAmount: 0
    });

    React.useEffect(() => {
        if (props.scheduleId > 0 && props.showExtendModal) {
            getData();
        }
    }, [props.scheduleId]);

    const getData = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/Allocate/GetExtendDetails?ScheduleId=' + props.scheduleId + '&JobQuoteItemId=' + props.jobQuoteItemId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var extendData: ExtendDetails = {
                scheduleId: res.data.scheduleId,
                typeId: res.data.typeId,
                itemId: res.data.itemId,
                item: res.data.item,
                lastHireDate: res.data.typeId === 1 ? res.data.lastInvoiceDate : "",
                existingLastHireDate: res.data.typeId === 1 ? res.data.lastInvoiceDateFormatted : "",
                offhireDate: res.data.offhireDate,
                existingOffhireDate: res.data.offhireDateFormatted,
                existingPickupDate: res.data.pickupDateFormatted,
                existingOverhireRate: res.data.overhireRate,
                lastInvoiceDate: res.data.lastInvoiceDate,
                lastInvoiceDateFormatted: res.data.lastInvoiceDateFormatted,
                accountTypeId: res.data.accountTypeId,
                purchaseOrderNumber: res.data.purchaseOrderNumber,
                extendNoDays: 0,
                newLastHireDate: "",
                newOffhireDate: "",
                newPickupDate: "",
                invoiceAmount: 0,
                discountPercent: 0,
                publicHolidays: 0,
                finalInvoiceAmount: 0
            };
            setExtendDetails(extendData);
            setLoading(false);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const resetExtendDetails = () => {
        var extDet: ExtendDetails = {
            scheduleId: 0,
            typeId: 0,
            itemId: 0,
            item: "",
            lastHireDate: "",
            existingLastHireDate: "",
            offhireDate: "",
            existingOffhireDate: "",
            existingPickupDate: "",
            existingOverhireRate: 0,
            lastInvoiceDate: "",
            lastInvoiceDateFormatted: "",
            accountTypeId: 0,
            purchaseOrderNumber: "",
            extendNoDays: 0,
            newLastHireDate: "",
            newOffhireDate: "",
            newPickupDate: "",
            invoiceAmount: 0,
            discountPercent: 0,
            publicHolidays: 0,
            finalInvoiceAmount: 0
        };

        return extDet;
    }

    const handleChange = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errorsList = errors;
        if (e.target.value) {
            delete errors[e.target.name];
            setErrors(errorsList);
        }

        setExtendDetails(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    //called if # days, offhire or discount is updated so I can recalcuate the price. Need to cater for Public Hols and Shutdown Periods for Scaffold (Company 2)
    const handleChangeCalcPrice = (e: any) => {
        //check if there is a value and an error, and if so remove from error list
        var errorsList = errors;
        if (e.target.value) {
            delete errorsList[e.target.name];
            setErrors(errorsList);
        }

        setExtendDetails(prevState => ({ ...prevState, [e.target.name]: e.target.value }));

        let lastDate = extendDetails.lastInvoiceDateFormatted === "" ? extendDetails.offhireDate : extendDetails.lastInvoiceDate;
        let newLastHireDate = extendDetails.newLastHireDate;
        let newOffhireDate = extendDetails.newOffhireDate;
        let extNoDays = extendDetails.extendNoDays;
        let discountPercent = extendDetails.discountPercent;
        if (e.target.name == "extendNoDays") {
            var daysToExtend = parseInt(e.target.value, 10);
            extNoDays = daysToExtend;

            if (extendDetails.typeId == 1) {
                //need to calculate new Last Hire Date
                newLastHireDate = DateHelpers.addDaysToDate(extendDetails.lastHireDate, daysToExtend);
                setExtendDetails(prevState => ({ ...prevState, newLastHireDate: newLastHireDate }));
                delete errorsList["newLastHireDate"];
            } else {
                //need to calculate new Offhire Date
                newOffhireDate = DateHelpers.addDaysToDate(lastDate, daysToExtend);
                setExtendDetails(prevState => ({ ...prevState, newOffhireDate: newOffhireDate }));
                delete errorsList["newOffhireDate"];
            }
        } else if (e.target.name === "newOffhireDate") {
            newOffhireDate = e.target.value;
            //need to calculate no of days 
            var nodays = DateHelpers.daysBetweenDates(lastDate, e.target.value);
            extNoDays = nodays;
            setExtendDetails(prevState => ({ ...prevState, extendNoDays: nodays }));
            delete errorsList["extendNoDays"];
        } else if (e.target.name === "newLastHireDate") {
            newLastHireDate = e.target.value;
            //need to calculate no of days
            var nodays = DateHelpers.daysBetweenDates(extendDetails.lastHireDate, e.target.value);
            extNoDays = nodays;
            setExtendDetails(prevState => ({ ...prevState, extendNoDays: nodays }));
            delete errorsList["extendNoDays"];
        } else if (e.target.name === "discountPercent") {
            discountPercent = e.target.value * 1;
        }

        delete errorsList["invoiceAmount"];
        delete errorsList["finalInvoiceAmount"];
        setErrors(errorsList);
        
        if (extNoDays > 0) {
            calculateAmount(lastDate, newLastHireDate, newOffhireDate, discountPercent);
        } else {
            setExtendDetails(prevState => ({ ...prevState, invoiceAmount: 0, finalInvoiceAmount: 0 }));

        }
    }

    const calculateAmount = async (lastInvoiceDate: string, newLastHireDate: string, newOffhireDate: string, discountPercent: number) => {
        //calculate invoice amount and final invoice amount
        const token = await authService.getAccessToken();
        //Note: need to add a day to get the new invoice period start date (will cater for this in the back end)
        var startDate = lastInvoiceDate != "" ? lastInvoiceDate : extendDetails.typeId === 1 ? extendDetails.lastHireDate : extendDetails.offhireDate;
        var endDate = extendDetails.typeId === 1 ? newLastHireDate : newOffhireDate;

        axios.get('api/Allocate/CalculateExtendAmount?StartDate=' + startDate + '&endDate=' + endDate + '&ScheduleId=' + extendDetails.scheduleId + '&overhireRate=' + extendDetails.existingOverhireRate + "&discountPercent=" + discountPercent, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setExtendDetails(prevState => ({ ...prevState, invoiceAmount: res.data.invoiceAmountFormatted, finalInvoiceAmount: res.data.finalInvoiceAmountFormatted, publicHolidays: res.data.publicHolidays }));
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const hideExtendModal = () => {
        setErrors({});
        var extDet = resetExtendDetails();
        setExtendDetails(extDet);
        props.hideModal();
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (extendDetails.extendNoDays <= 0) {
            formIsValid = false;
            errorList["extendNoDays"] = "No Days to Extend is required";
        }

        if (extendDetails.typeId == 1) {
            //INSTALL
            if (!extendDetails.newLastHireDate) {
                formIsValid = false;
                errorList["newLastHireDate"] = "Last Hire Date is required";
            } else if (!DateHelpers.isValidDate(extendDetails.newLastHireDate)) {
                formIsValid = false;
                errorList["newLastHireDate"] = "Last Hire Date must be a valid date";
            } else if (DateHelpers.daysBetweenDates(extendDetails.lastHireDate, extendDetails.newLastHireDate) <= 0) {
                formIsValid = false;
                errorList["newLastHireDate"] = "Last Hire Date must > Existing Last Hire Date";
            }
        } else {
            //PICKUP
            if (!extendDetails.newOffhireDate) {
                formIsValid = false;
                errorList["newOffhireDate"] = "Offhire Date is required";
            } else if (!DateHelpers.isValidDate(extendDetails.newOffhireDate)) {
                formIsValid = false;
                errorList["newOffhireDate"] = "Offhire Date must be a valid date";
            } else if (DateHelpers.daysBetweenDates(extendDetails.offhireDate, extendDetails.newOffhireDate) <= 0) {
                formIsValid = false;
                errorList["newOffhireDate"] = "Offhire Date must > Existing Offhire Date";
            }

            if (!extendDetails.newPickupDate) {
                formIsValid = false;
                errorList["newPickupDate"] = "Pickup Date is required";
            } else if (!DateHelpers.isValidDate(extendDetails.newPickupDate)) {
                formIsValid = false;
                errorList["newPickupDate"] = "Pickup Date must be a valid date";
            } else if (DateHelpers.daysBetweenDates(extendDetails.offhireDate, extendDetails.newPickupDate) <= 0) {
                formIsValid = false;
                errorList["newPickupDate"] = "Pickup Date must be > Existing Offhire Date";
            }
        }

        if (extendDetails.finalInvoiceAmount <= 0) {
            formIsValid = false;
            errorList["finalInvoiceAmount"] = "Final Invoice Amount is required";
        }

        if (extendDetails.accountTypeId == 3 && !extendDetails.purchaseOrderNumber) {
            formIsValid = false;
            errorList["purchaseOrderNumber"] = "Purchase Order Number is required";
        }

        setErrors(errorList);
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            saveExtend();
        }
    }

    const saveExtend = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var allocate = {
            scheduleId: extendDetails.scheduleId,
            item: extendDetails.item,
            typeId: extendDetails.typeId,
            existLastHireDate: extendDetails.lastHireDate,
            lastHireDate: extendDetails.newLastHireDate,
            offhireDate: extendDetails.newOffhireDate,
            pickupDate: extendDetails.newPickupDate,
            purchaseOrderNumber: extendDetails.purchaseOrderNumber,
            noDays: extendDetails.extendNoDays,
            invoiceAmount: extendDetails.invoiceAmount,
            discountPercent: extendDetails.discountPercent,
            finalInvoiceAmount: extendDetails.finalInvoiceAmount,
            subId: user.sub
        };

        axios.post('api/Allocate/ExtendAllocationItem', allocate, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setErrors({});
            var extDet = resetExtendDetails();
            setExtendDetails(extDet);
            props.save();
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const minDate = DateHelpers.addDaysToDate(extendDetails.offhireDate, 1);

    const renderDetails = (
        <div className="static-modal">
            <Modal show={props.showExtendModal} onHide={hideExtendModal}>
                <Modal.Header className="modal__header--colour">
                    <Modal.Title>Extend hire period for {extendDetails.item}</Modal.Title>
                    <button type="button" className="close" onClick={hideExtendModal}>&times;</button>
                </Modal.Header>
                <Modal.Body>
                    <div className={loading ? "" : "hidden" }>
                        <p><em>Loading...</em></p>
                    </div>
                    <div className={loading ? "hidden" : ""}>
                        <div className={extendDetails.typeId == 1 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="existingLastHireDate">
                                <span className="label">Original Last Date of Hire</span>
                                <input type='text' id="existingLastHireDate" name="existingLastHireDate" className='input allocate__extendwidth' value={extendDetails.existingLastHireDate} disabled></input>
                            </label>
                        </div>
                        <div className={extendDetails.typeId == 2 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="existingOffhireDate">
                                <span className="label">Existing Offhire Date</span>
                                <input type='text' id="existingOffhireDate" name="existingOffhireDate" className='input allocate__extendwidth' value={extendDetails.existingOffhireDate} disabled></input>
                            </label>
                        </div>
                        <div className={extendDetails.typeId == 2 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="existingPickupDate">
                                <span className="label">Existing Pickup Date</span>
                                <input type='text' id="existingPickupDate" name="existingPickupDate" className='input allocate__extendwidth' value={extendDetails.existingPickupDate} disabled></input>
                            </label>
                        </div>
                        <div className={extendDetails.typeId == 2 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="lastInvoiceDateFormatted">
                                <span className="label">Last Invoice End Date</span>
                                <input type='text' id="lastInvoiceDateFormatted" name="lastInvoiceDateFormatted" className='input allocate__extendwidth' value={extendDetails.lastInvoiceDateFormatted} disabled></input>
                            </label>
                        </div>
                        <div className="">
                            <label className="input-group" htmlFor="purchaseOrderNumber">
                                <span className="label">Purchase Order Number</span>
                                <input type='text' id="purchaseOrderNumber" name="purchaseOrderNumber" className='input allocate__extendwidth' value={extendDetails.purchaseOrderNumber} onChange={(e) => handleChange(e)}></input>
                            </label>
                            <span className="label errors">{errors["purchaseOrderNumber"]}</span>
                        </div>
                        <div className="">
                            <label className="input-group" htmlFor="extendNoDays">
                                <span className="label">No Days to Extend</span>
                                <input type='number' id="extendNoDays" name="extendNoDays" className='input allocate__extendwidth' value={extendDetails.extendNoDays} onChange={(e) => handleChangeCalcPrice(e)}></input>
                            </label>
                            <span className="label errors">{errors["extendNoDays"]}</span>
                        </div>
                        <div className={extendDetails.typeId == 1 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="newLastHireDate">
                                <span className="label">Last Date of Hire</span>
                                <input type='date' min={minDate} id="newLastHireDate" name="newLastHireDate" className='input allocate__extendwidth' value={extendDetails.newLastHireDate} onChange={(e) => handleChangeCalcPrice(e)}></input>
                            </label>
                            <span className="label errors">{errors["newOffhireDate"]}</span>
                        </div>
                        <div className={extendDetails.typeId == 2 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="newOffhireDate">
                                <span className="label">Offhire Date</span>
                                <input type='date' min={minDate} id="newOffhireDate" name="newOffhireDate" className='input allocate__extendwidth' value={extendDetails.newOffhireDate} onChange={(e) => handleChangeCalcPrice(e)}></input>
                            </label>
                            <span className="label errors">{errors["newOffhireDate"]}</span>
                        </div>
                        <div className={extendDetails.typeId == 2 ? "" : "hidden"}>
                            <label className="input-group" htmlFor="newPickupDate">
                                <span className="label">Pickup Date</span>
                                <input type='date' min={minDate} id="newPickupDate" name="newPickupDate" className='input allocate__extendwidth' value={extendDetails.newPickupDate} onChange={(e) => handleChange(e)}></input>
                            </label>
                            <span className="label errors">{errors["newPickupDate"]}</span>
                        </div>
                        <div className="">
                            <label className="input-group" htmlFor="overhireRate">
                                <span className="label">Overhire Rate</span>
                                <input type='text' id="existingOverhireRate" name="existingOverhireRate" className='input allocate__extendwidth' value={extendDetails.existingOverhireRate} disabled></input>
                            </label>
                        </div>
                        <div className="">
                            <label className="input-group" htmlFor="invoiceAmount">
                                <span className="label">Invoice Amount</span>
                                <input type='text' id="invoiceAmount" name="invoiceAmount" className='input allocate__extendwidth' value={extendDetails.invoiceAmount} disabled></input>
                            </label>
                        </div>
                        <div className="">
                            <label className="input-group" htmlFor="discountPercent">
                                <span className="label">Discount % (10 = 10%)</span>
                                <input type='number' id="discountPercent" name="discountPercent" className='input allocate__extendwidth' value={extendDetails.discountPercent} onChange={(e) => handleChangeCalcPrice(e)}></input>
                            </label>
                            <span className="label errors">{errors["discountPercent"]}</span>
                        </div>
                        <div className="">
                            <label className="input-group" htmlFor="finalInvoiceAmount">
                                <span className="label">Final Invoice Amount</span>
                                <input type='number' id="finalInvoiceAmount" name="finalInvoiceAmount" className='input allocate__extendwidth' value={extendDetails.finalInvoiceAmount} onChange={(e) => handleChange(e)}></input>
                            </label>
                            <span className="label errors">{errors["finalInvoiceAmount"]}</span>
                        </div>
                        <div className={extendDetails.publicHolidays === 0 ? "hidden" : ""}>
                            <span className="allocate__extendNote makeitred">Note: Final Invoice Amount excludes {extendDetails.publicHolidays} public holidays / shutdown period days within the invoice period.</span>
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <div className="allocate__extendNote">
                        <span>NOTE: When you click Save, an Overhire Invoice will be created for the Final Invoice Amount.</span>
                    </div>
                    <button className="popup-button--yes" onClick={save}>Save</button>
                </Modal.Footer>
            </Modal>
        </div>
    );
    
    let contents = renderDetails;

    return (
        <div>
            {contents}
        </div>
    );

}

export default AllocateExtendModal;